import { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  getCurrenciesApi,
  getLanguagesApi,
  getTenantRegionSettingsApi,
  updateTenantRegionSettingsApi,
} from "../apis/regionApis";
import { AppContext } from "../../../store";

export const useRegionConfig = ({
  isAllLanguagesOnly = false,
  isAllLanguagesOnlyEnable = true,
}) => {
  const [state, setState] = useImmer({
    formData: {
      currency: {
        currency_code: "",
        minimum_denomination: null,
        symbol: "",
        symbol_placement: "",
        currency_precision: null,
        rounding_mode: "",
        show_currency: false,
      },
      languages: [],
      timezone: {
        code: "",
        name: "",
      },
    },
    defaultLanguage: null,
    isLoading: true,
    isSubmitting: false,
    hasChanges: false,
    languagesOptions: [],
    currenciesOptions: [],
  });

  const { setAppState } = useContext(AppContext);

  useEffect(() => {
    const fetchRegionSettings = async () => {
      try {
        const response = await getTenantRegionSettingsApi();
        if (!response?.success) {
          throw new Error(
            response?.message || "Failed to load regional settings",
          );
        }

        setState((draft) => {
          const { languages: allLanguages, ...rest } = response.data;
          const { defaultLanguage, languages } = getFilterLanguages(
            allLanguages || [],
          );
          draft.formData = {
            ...rest,
            languages: languages,
          };
          draft.defaultLanguage = defaultLanguage;
        });
      } catch (error) {
        errorMessage(
          error.response?.data?.message ||
            error.message ||
            "Failed to load regional settings",
        );
      } finally {
        setState((draft) => {
          draft.isLoading = false;
        });
      }
    };
    if (isAllLanguagesOnly) {
      if (isAllLanguagesOnlyEnable) fetchLang();
    } else {
      fetchRegionSettings();
      fetchLang();
      fetchCurrencies();
    }
  }, [setState]);

  const getFilterLanguages = (allLanguages) => {
    return {
      defaultLanguage: allLanguages.find((lang) => lang.default),
      languages: allLanguages.filter((lang) => !lang.default),
    };
  };

  const fetchLang = async () => {
    try {
      const res = await getLanguagesApi();
      if (res.success) {
        setState((draft) => {
          draft.languagesOptions = (res.data || []).map((language) => ({
            ...language,
            code: language.value,
            name: language.label,
          }));
        });
      } else {
        errorMessage(res.message || "Failed to load languages");
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message ||
          error.message ||
          "Failed to load languages",
      );
    }
  };

  const fetchCurrencies = async () => {
    try {
      const res = await getCurrenciesApi();
      if (res.success) {
        setState((draft) => {
          draft.currenciesOptions = res.data || [];
        });
      } else {
        errorMessage(res.message || "Failed to load currencies");
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message ||
          error.message ||
          "Failed to load currencies",
      );
    }
  };

  const onCurrencyChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.formData.currency[name] =
        name === "currency_precision" ? Number(value) : value;
      draft.hasChanges = true;
    });
  };

  const onShowCurrencyChange = (event) => {
    setState((draft) => {
      draft.formData.currency.show_currency = event.target.checked;
      draft.hasChanges = true;
    });
  };

  const onCurrencyOptionChange = (_, currency) => {
    if (!currency) return;

    setState((draft) => {
      draft.formData.currency.currency_code = currency.value;
      draft.formData.currency.symbol = currency.symbol;
      draft.hasChanges = true;
    });
  };

  const onLanguageChange = (_, language) => {
    setState((draft) => {
      draft.formData.languages = language ? [language] : [];
      draft.hasChanges = true;
    });
  };

  const onOtherLanguageChange = (_, language) => {
    setState((draft) => {
      draft.formData.languages = language ? [language] : [];
      draft.hasChanges = true;
    });
  };

  const onTimezoneChange = (timezoneOption) => {
    if (!timezoneOption) return;

    setState((draft) => {
      draft.formData.timezone = {
        ...draft.formData.timezone,
        code: timezoneOption.value,
        name: timezoneOption.label,
        tz_name: timezoneOption.value,
      };
      draft.hasChanges = true;
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    try {
      setState((draft) => {
        draft.isSubmitting = true;
      });

      let defaultLang = state.defaultLanguage;

      if (!defaultLang) {
        defaultLang = state.languagesOptions.find((lang) => lang.default);
      }

      const payload = {
        ...state.formData,
        languages: [defaultLang, ...state.formData.languages],
      };

      const response = await updateTenantRegionSettingsApi(payload);
      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to update regional settings",
        );
      }
      setAppState((draft) => {
        draft.refetchAppSettings = true;
      });

      setState((draft) => {
        const { languages: allLanguages, ...rest } = response.data;
        const { defaultLanguage, languages } = getFilterLanguages(
          allLanguages || [],
        );
        draft.formData = {
          ...rest,
          languages: languages,
        };
        draft.defaultLanguage = defaultLanguage;
        draft.hasChanges = false;
      });
      successMessage(
        response.message ||
          "Currency, language and timezone settings updated successfully",
      );
    } catch (error) {
      errorMessage(
        error.response?.data?.message ||
          error.message ||
          "Failed to update regional settings",
      );
    } finally {
      setState((draft) => {
        draft.isSubmitting = false;
      });
    }
  };

  return {
    state,
    onCurrencyChange,
    onShowCurrencyChange,
    onCurrencyOptionChange,
    onLanguageChange,
    onOtherLanguageChange,
    onTimezoneChange,
    onSubmit,
  };
};
